import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Collapse,
  Divider,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  alpha,
  Chip,
  Avatar,
  Button,
} from '@mui/material';
import {
  Dashboard,
  CloudUpload,
  People,
  CalendarMonth,
  Assessment,
  Settings,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
  ExpandMore,
  EventNote,
  Upload,
  PersonAdd,
  Schedule,
  ViewList,
  BarChart,
  Person,
  Notifications as NotificationsIcon,
  ExitToApp,
  NewReleases,
  Logout as LogoutIcon,
  AccessTime,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import authService from '../../services/authService';
import { MederiAdminBrand } from '../../design-system/MederiAdminBrand';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path?: string;
  badge?: number;
  children?: MenuItem[];
  requiredPermission?: string;
  isNew?: boolean;
  isComingSoon?: boolean;
}

interface NavigationSidebarProps {
  open: boolean;
  onToggle: () => void;
}

const SidebarPanelIcon: React.FC = () => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="2.5" y="3" width="19" height="18" rx="4" stroke="currentColor" strokeWidth="1.8" /><path d="M9 3.5v17" stroke="currentColor" strokeWidth="1.8" /><rect x="4.6" y="5.2" width="2.3" height="13.6" rx="1" fill="currentColor" /></svg>;

const menuItems: MenuItem[] = [
  // ダッシュボードページを非表示
  // {
  //   id: 'dashboard',
  //   label: 'ダッシュボード',
  //   icon: <Dashboard />,
  //   path: '/dashboard',
  // },
  {
    id: 'appointments',
    label: '予約管理',
    icon: <EventNote />,
    children: [
      {
        id: 'appointment-list',
        label: '診療予約一覧',
        icon: <ViewList />,
        path: '/appointments/list',
      },
      {
        id: 'out-of-hours-list',
        label: '時間外予約一覧',
        icon: <AccessTime />,
        path: '/appointments/out-of-hours',
      },
      {
        id: 'appointment-upload',
        label: '予約CSVアップロード',
        icon: <CloudUpload />,
        path: '/appointments/upload',
      },
      {
        id: 'appointment-calendar',
        label: '予約カレンダー',
        icon: <CalendarMonth />,
        path: '/appointments/calendar',
        isComingSoon: true,
      },
    ],
  },
  {
    id: 'doctors',
    label: '医師管理',
    icon: <People />,
    children: [
      {
        id: 'doctor-list',
        label: '医師一覧',
        icon: <PersonAdd />,
        path: '/doctors/list',
        requiredPermission: 'admin',
      },
      {
        id: 'doctor-shifts',
        label: 'シフト管理',
        icon: <Schedule />,
        path: '/doctors/shifts',
      },
      {
        id: 'shift-rules',
        label: 'シフトルール',
        icon: <EventNote />,
        path: '/doctors/shift-rules',
        requiredPermission: 'admin',
      },
      {
        id: 'shift-manager',
        label: '月次シフト管理',
        icon: <CalendarMonth />,
        path: '/doctors/shifts/manager',
        requiredPermission: 'admin',
      },
      {
        id: 'reservation-slots',
        label: '解放枠管理',
        icon: <NewReleases />,
        path: '/doctors/reservation-slots',
        requiredPermission: 'admin',
      },
      {
        id: 'shift-upload',
        label: 'シフトCSVアップロード',
        icon: <Upload />,
        path: '/doctors/shift-upload',
      },
      {
        id: 'shift-calendar',
        label: 'シフトカレンダー',
        icon: <CalendarMonth />,
        path: '/doctors/shift-calendar',
        isComingSoon: true,
      },
    ],
  },
  {
    id: 'content', label: 'マイページ管理', icon: <NotificationsIcon />,
    requiredPermission: 'admin-or-operator',
    children: [
      { id: 'notices', label: 'お知らせ管理', icon: <NewReleases />, path: '/content/notices' },
      { id: 'banners', label: 'バナー管理', icon: <ViewList />, path: '/content/banners' },
      { id: 'withdrawal-questionnaires', label: '解約アンケート管理', icon: <Assessment />, path: '/content/withdrawal-questionnaires' },
    ],
  },
  {
    id: 'user-management',
    label: 'アカウント管理',
    icon: <Person />,
    requiredPermission: 'admin',
    children: [
      {
        id: 'account-list',
        label: 'アカウント一覧',
        icon: <Person />,
        path: '/settings/account',
      },
    ],
  },
  // レポート・分析ページを非表示
  // {
  //   id: 'reports',
  //   label: 'レポート・分析',
  //   icon: <Assessment />,
  //   children: [
  //     {
  //       id: 'appointment-stats',
  //       label: '予約統計',
  //       icon: <BarChart />,
  //       path: '/reports/appointments',
  //     },
  //     {
  //       id: 'doctor-utilization',
  //       label: '医師稼働率',
  //       icon: <BarChart />,
  //       path: '/reports/utilization',
  //     },
  //     {
  //       id: 'monthly-report',
  //       label: '月次レポート',
  //       icon: <Assessment />,
  //       path: '/reports/monthly',
  //       isComingSoon: true,
  //     },
  //   ],
  // },
  // システム設定ページを非表示
  // {
  //   id: 'settings',
  //   label: 'システム設定',
  //   icon: <Settings />,
  //   children: [
  //     {
  //       id: 'account',
  //       label: 'アカウント管理',
  //       icon: <AccountCircle />,
  //       path: '/settings/account',
  //     },
  //     {
  //       id: 'notifications',
  //       label: '通知設定',
  //       icon: <NotificationsIcon />,
  //       path: '/settings/notifications',
  //     },
  //     {
  //       id: 'export',
  //       label: 'データエクスポート',
  //       icon: <ExitToApp />,
  //       path: '/settings/export',
  //     },
  //   ],
  // },
];

const NavigationSidebar: React.FC<NavigationSidebarProps> = ({ open, onToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedItems, setExpandedItems] = useState<string[]>(['appointments', 'doctors', 'user-management', 'content']);
  const { user, logout } = useAuth();
  const [userManagementVisible, setUserManagementVisible] = useState(false);

  useEffect(() => {
    authService.getConfig().then(config => setUserManagementVisible(config.user_management_visible))
      .catch(() => setUserManagementVisible(false));
  }, []);

  const drawerWidth = isMobile ? 280 : open ? 196 : 68;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('ログアウトエラー:', error);
    }
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.isComingSoon) {
      // 開発中の機能は通知表示
      return;
    }

    if (item.path) {
      navigate(item.path);
      if (isMobile) {
        onToggle();
      }
    } else if (item.children) {
      setExpandedItems((prev) =>
        prev.includes(item.id)
          ? prev.filter((id) => id !== item.id)
          : [...prev, item.id]
      );
    }
  };

  const isItemActive = (item: MenuItem): boolean => {
    const currentPath = `${location.pathname}${location.search}`;
    if (item.path) {
      return currentPath === item.path || (!item.path.includes('?') && location.pathname === item.path);
    }
    if (item.children) {
      return item.children.some((child) => child.path === currentPath || child.path === location.pathname);
    }
    return false;
  };

  const renderMenuItem = (item: MenuItem, depth = 0) => {
    if (item.id === 'user-management' && !userManagementVisible) return null;
    // 権限チェック: requiredPermissionが設定されている場合、ユーザーのロールと一致しなければ非表示
    if (item.requiredPermission && user?.role !== item.requiredPermission
      && !(item.requiredPermission === 'admin-or-operator' && (user?.role === 'admin' || user?.role === 'operator'))) {
      return null;
    }

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isActive = isItemActive(item);

    return (
      <React.Fragment key={item.id}>
        <ListItem
          disablePadding
          sx={{
            display: 'block',
            px: depth > 0 ? 0.5 : 0,
          }}
        >
          <ListItemButton
            title={item.isComingSoon ? `${item.label}（近日公開）` : item.label}
            onClick={() => handleItemClick(item)}
            sx={{
              minHeight: depth > 0 ? 32 : 38,
              justifyContent: open ? 'initial' : 'center',
              px: 1,
              pl: depth > 0 ? 1 : 1,
              borderRadius: '7px',
              mx: depth > 0 ? 0 : 0.5,
              mb: depth > 0 ? 0.25 : 0.5,
              backgroundColor: isActive && depth > 0 ? '#4b4643' : 'transparent',
              color: isActive && depth > 0 ? '#ffffff' : depth > 0 ? '#bcb5b0' : '#e9e4e0',
              '&:hover': {
                backgroundColor: '#393633',
              },
              ...(item.isComingSoon && {
                opacity: 0.6,
                cursor: 'not-allowed',
              }),
            }}
          >
            {(depth === 0 || !open) && <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 1 : 'auto',
                justifyContent: 'center',
                color: isActive && !open ? '#f7c9c7' : '#bcb5b0',
              }}
            >
              {item.badge ? (
                <Badge badgeContent={item.badge} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>}
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: open ? 1 : 0,
                      fontWeight: isActive || depth === 0 ? 700 : 500,
                      fontSize: depth === 0 ? 12 : 11,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.label}
                  </Typography>
                  {item.isNew && open && (
                    <Chip
                      label="New"
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              }
              sx={{ opacity: open ? 1 : 0, minWidth: 0 }}
            />
            {hasChildren && open && (
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <ExpandMore />
              </motion.div>
            )}
            {isActive && depth > 0 && open && <Box sx={{ width: 5, height: 5, ml: 'auto', borderRadius: '50%', bgcolor: '#f7c9c7', flexShrink: 0 }} />}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ ml: 2.5, pl: 1.25, mb: 1 }}>
              {item.children!.map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#272522',
        color: '#e9e4e0',
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          px: open ? 1.5 : 0.5,
          pt: 2,
          pb: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, '& .mederi-admin-brand': { '--brand-logo-width': '62px', '--brand-gap': '9px' } }}>
          {open ? <MederiAdminBrand variant="dark" /> : <img src={`${process.env.PUBLIC_URL}/favicon.ico`} alt="mederi" width={28} height={28} />}
        </Box>
        {!isMobile && (
          <IconButton onClick={onToggle} size="small" aria-label={open ? 'サイドメニューを折りたたむ' : 'サイドメニューを展開'} sx={{ color: '#bcb5b0' }}>
            <SidebarPanelIcon />
          </IconButton>
        )}
      </Box>

      {/* メニューリスト */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        <List>{menuItems.map((item) => renderMenuItem(item))}</List>
      </Box>

      {/* フッター */}
      <Box
        sx={{
          px: 1.5,
          pt: 1.5,
          pb: 1,
          borderTop: '1px solid #48433f',
        }}
      >
        {/* ユーザー情報 */}
        {user && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <Avatar
                sx={{
                  width: 29,
                  height: 29,
                  borderRadius: '7px',
                  bgcolor: '#e9c0bd',
                  color: '#493a36',
                  fontSize: '0.8rem',
                }}
              >
                {user.name ? user.name.charAt(0) : 'U'}
              </Avatar>
              {open && (
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {user.name}
                  </Typography>
                </Box>
              )}
            </Box>
            {/* ログアウトボタン */}
            <Tooltip title={!open ? 'ログアウト' : ''} placement="right">
              <Button
                fullWidth={open}
                size="small"
                variant="text"
                color="inherit"
                onClick={handleLogout}
                startIcon={open ? <LogoutIcon /> : undefined}
                sx={{
                  minWidth: open ? 'auto' : 40,
                  height: 32,
                  justifyContent: open ? 'flex-start' : 'center',
                  px: open ? 1 : 0,
                  color: '#e9e4e0',
                  '&:hover': { backgroundColor: '#393633' },
                }}
              >
                {open ? 'ログアウト' : <LogoutIcon fontSize="small" />}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={isMobile ? onToggle : undefined}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          border: 'none',
          boxShadow: `2px 0 8px ${alpha(theme.palette.common.black, 0.12)}`,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default NavigationSidebar;
